import React, { useState } from "react";
import {
    Box,
    useColorModeValue,
    Button,
    Flex,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Select,
    Text,
    useDisclosure,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    IconButton,
    Tooltip,
    VStack,
    HStack,
    useToast,
} from "@chakra-ui/react";
import { CopyIcon } from "@chakra-ui/icons";
import * as XLSX from "xlsx";
import Card from "components/card/Card.js";


const Index = ({ keywords, onMappingSubmit }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isSkippedOpen, onOpen: onSkippedOpen, onClose: onSkippedClose } = useDisclosure();
    const [columns, setColumns] = useState([]);
    const [mapping, setMapping] = useState({ url: "", keyword_name: "", prod_code: "", seller_name: null, mrp: 0, image_link: null });
    const [fileData, setFileData] = useState([]);
    const [skippedData, setSkippedData] = useState([]);
    const [insertionStats, setInsertionStats] = useState(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const userInfo = JSON.parse(localStorage.getItem("PAuser"));
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    // Add selectedFile state
    const [selectedFile, setSelectedFile] = useState(null);

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file); // Save the file object
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                // Read the file to extract column headers only
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                if (jsonData.length > 0) {
                    setColumns(jsonData[0]);
                    onOpen();
                }
                event.target.value = null;
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const handleMappingSubmit = async () => {
        if (!selectedFile) return;

        const hasSellerName = mapping.seller_name && mapping.seller_name.trim() !== "";

        // Use FormData to send file + JSON configs
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("mapping", JSON.stringify(mapping));
        formData.append("keywords", JSON.stringify(keywords));
        formData.append("uid", userInfo?.message?.result.id);
        formData.append("hasSellerName", hasSellerName);

        onClose(); // Close mapping modal

        try {
            setIsLoading(true);
            const response = await fetch(`${backendUrl}/api/products/bulkInsertMappedExcel`, {
                method: "POST",
                body: formData, // the browser will automatically set the boundary for multipart/form-data
            });

            const responseData = await response.json();

            // Handle validation and internal errors
            if (!response.ok && response.status !== 207) {
                if (responseData.invalidKeywords) {
                    // Truncate error display if there are many bad rows
                    const errorMessage = responseData.invalidKeywords
                        .slice(0, 10)
                        .map(item => `Row ${item.rowNumber}: "${item.keywordName}" - ${item.reason}`)
                        .join("\n") + (responseData.invalidKeywords.length > 10 ? `\n...and ${responseData.invalidKeywords.length - 10} more.` : "");

                    toast({
                        title: "Invalid Keywords Found",
                        description: errorMessage,
                        status: "error",
                        duration: 6000,
                        isClosable: true,
                    });
                } else {
                    toast({
                        title: "Error",
                        description: responseData.message || "Failed to process file",
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                    });
                }
                return;
            }

            // Success Handling
            if (hasSellerName) {
                setInsertionStats({
                    inserted: responseData.productSellerMappingInserted || 0,
                    skipped: responseData.skippedProducts || 0,
                    duplicateCodes: responseData.duplicateProdCodes || []
                });

                if (responseData.skippedDataDetails && responseData.skippedDataDetails.length > 0) {
                    setSkippedData(responseData.skippedDataDetails); // Populate skipped rows modal
                    onSkippedOpen();
                } else {
                    toast({
                        title: "Success",
                        description: `Successfully inserted ${responseData.productSellerMappingInserted || 0} products!`,
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                    if (onMappingSubmit) {
                        onMappingSubmit(null, { isExcelUpload: true, message: "Data saved successfully with seller verification!" });
                    }
                }
            } else {
                toast({
                    title: "Success",
                    description: "Data saved successfully",
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                });
                if (onMappingSubmit) {
                    onMappingSubmit(null, { isExcelUpload: true, message: "Data saved successfully!" });
                }
            }

        } catch (error) {
            toast({
                title: "Error",
                description: `An error occurred: ${error.message}`,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
            setSelectedFile(null); // Reset selection
        }
    };

    const handleMappingChange = (e) => {
        const { name, value } = e.target;
        setMapping((prev) => ({ ...prev, [name]: value }));
    };

    // const handleMappingSubmit = () => {
    //     const mappedData = fileData
    //         .slice(1)
    //         .filter((row) => {
    //             if (!row || !Array.isArray(row) || row.length === 0) return false;
    //             return row.some(
    //                 (cell) => cell !== undefined && cell !== null && String(cell).trim() !== ""
    //             );
    //         });

    //     if (mappedData.length === 0) {
    //         toast({
    //             title: "Error",
    //             description: "No valid data found in the spreadsheet.",
    //             status: "error",
    //             duration: 4000,
    //             isClosable: true,
    //         });
    //         return;
    //     }

    //     const keywordIdIndex = columns.indexOf(mapping.keyword_name);

    //     // VALIDATION STEP: Check all keywords BEFORE mapping
    //     const invalidKeywords = [];
    //     for (let i = 0; i < mappedData.length; i++) {
    //         const row = mappedData[i];
    //         const keywordName = row[keywordIdIndex] || "";

    //         if (keywordName.trim() === "") {
    //             invalidKeywords.push({
    //                 rowNumber: i + 2, // +2 because row 1 is header, array starts at 0
    //                 keywordName: keywordName,
    //                 reason: "Keyword name is empty"
    //             });
    //             continue;
    //         }

    //         const keywordId = keywords.find(
    //             (it) => it.name.toLowerCase().trim() === String(keywordName).toLowerCase().trim()
    //         )?.id;

    //         if (keywordId === undefined) {
    //             invalidKeywords.push({
    //                 rowNumber: i + 2,
    //                 keywordName: keywordName,
    //                 reason: "Keyword not found in system"
    //             });
    //         }
    //     }

    //     // If any invalid keywords found, show error and exit
    //     if (invalidKeywords.length > 0) {
    //         const errorMessage = invalidKeywords
    //             .map(item => `Row ${item.rowNumber}: "${item.keywordName}" - ${item.reason}`)
    //             .join("\n");

    //         toast({
    //             title: "❌ Invalid Keywords Found",
    //             description: errorMessage,
    //             status: "error",
    //             duration: 6000,
    //             isClosable: true,
    //         });
    //         return; // EXIT EARLY - don't continue
    //     }

    //     // NOW map the data (all keywords are validated)
    //     const finalMappedData = mappedData.map((row) => {
    //         const urlIndex = columns.indexOf(mapping.url);
    //         const prodCodeIndex = columns.indexOf(mapping.prod_code);
    //         const sellerNameIndex = columns.indexOf(mapping.seller_name);
    //         const mrpIndex = columns.indexOf(mapping.mrp);
    //         const image_link = columns.indexOf(mapping.image_link);

    //         const keywordName = row[keywordIdIndex] || "";
    //         const keywordId = keywords.find(
    //             (it) => it.name.toLowerCase().trim() === String(keywordName).toLowerCase().trim()
    //         )?.id;

    //         return {
    //             url: urlIndex !== -1 && row[urlIndex] ? row[urlIndex] : "",
    //             keyword_id: keywordId || "",
    //             prod_code: prodCodeIndex !== -1 && row[prodCodeIndex] ? row[prodCodeIndex] : "",
    //             seller_name: sellerNameIndex !== -1 && row[sellerNameIndex] ? row[sellerNameIndex] : "",
    //             mrp: mrpIndex !== -1 && row[mrpIndex] ? row[mrpIndex] : 0,
    //             image_link: image_link !== -1 && row[image_link] ? row[image_link] : ""
    //         };
    //     });

    //     // Check if seller_name column is mapped (and not empty string)
    //     const hasSellerName = mapping.seller_name && mapping.seller_name.trim() !== "";

    //     if (hasSellerName) {
    //         // Call bulk insert endpoint (with seller verification)
    //         callBulkInsertEndpoint(finalMappedData);
    //     } else {
    //         // Call regular add listing endpoint
    //         if (onMappingSubmit) {
    //             onMappingSubmit(finalMappedData);
    //         }
    //     }

    //     onClose();
    // };

    const callBulkInsertEndpoint = async (mappedData) => {
        try {
            setIsLoading(true);
            const response = await fetch(
                `${backendUrl}/api/products/bulkInsert`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ data: mappedData, uid: userInfo?.message?.result.id }),
                }
            );

            const responseData = await response.json();
            console.log("Response status:", response.status);
            console.log("Response data:", responseData);

            if (response.status === 200 && responseData.success) {
                console.log("Bulk insert successful:", responseData);

                setInsertionStats({
                    inserted: responseData.productSellerMappingInserted,
                    skipped: responseData.skippedProducts || 0,
                    duplicateCodes: responseData.duplicateProdCodes || []
                });

                // Build skipped data from mappedData and duplicateProdCodes
                if (responseData.duplicateProdCodes && responseData.duplicateProdCodes.length > 0) {
                    const duplicateCodesList = responseData.duplicateProdCodes.map(d => d.prod_code);
                    const skipped = mappedData.filter(item =>
                        duplicateCodesList.includes(item.prod_code)
                    );
                    setSkippedData(skipped);
                    onSkippedOpen();
                } else {
                    toast({
                        title: "Success",
                        description: `✅ Successfully inserted ${responseData.productSellerMappingInserted} products!`,
                        status: "success",
                        duration: 4000,
                        isClosable: true,
                    });
                    if (onMappingSubmit) {
                        onMappingSubmit(mappedData, { isBulkInsert: true, response: responseData });
                    }
                }
            } else if (response.status === 207) {
                // Partial insertion
                setInsertionStats({
                    inserted: responseData.productSellerMappingInserted,
                    skipped: responseData.skippedProducts || 0,
                    duplicateCodes: responseData.duplicateProdCodes || []
                });

                if (responseData.duplicateProdCodes && responseData.duplicateProdCodes.length > 0) {
                    const duplicateCodesList = responseData.duplicateProdCodes.map(d => d.prod_code);
                    const skipped = mappedData.filter(item =>
                        duplicateCodesList.includes(item.prod_code)
                    );
                    setSkippedData(skipped);
                    onSkippedOpen();
                }
            } else {
                console.error("Bulk insert failed:", responseData);
                toast({
                    title: "Error",
                    description: `❌ Failed to insert data:\n${responseData.message}`,
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error in bulk insert:", error);
            toast({
                title: "Error",
                description: `❌ An error occurred while inserting data:\n${error.message}`,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast({
            title: "Copied",
            description: "Product code copied to clipboard",
            status: "success",
            duration: 2000,
            isClosable: true,
        });
    };

    const downloadSkippedAsExcel = () => {
        const exportData = skippedData.map(item => ({
            "URL": item.url,
            "Product Code": item.prod_code,
            "Seller Name": item.seller_name,
            "Reason": "Multiple IDs found in database"
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Skipped Products");
        XLSX.writeFile(wb, "skipped_products.xlsx");
    };

    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

    return (
        <Box>
            <Flex justifyContent="flex-end">
                <Button colorScheme="blue" as="label" htmlFor="file-upload" isLoading={isLoading}>
                    Upload Spreadsheet
                </Button>
                <input
                    type="file"
                    id="file-upload"
                    style={{ display: "none" }}
                    accept=".xls,.xlsx,.csv"
                    onChange={handleFileUpload}
                />
            </Flex>
            <Flex justifyContent="flex-end">
                <Text>*Map your spreadsheet columns to the required fields.</Text>
            </Flex>

            {/* Modal for Column Mapping */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Map Columns</ModalHeader>
                    <ModalBody>
                        <Text mb={2}>Please map the spreadsheet columns to the following fields:</Text>
                        <Box mb={4}>
                            <Text mb={1}>URL</Text>
                            <Select name="url" value={mapping.url} onChange={handleMappingChange} placeholder="Select column">
                                {columns.map((col, index) => (
                                    <option key={index} value={col}>
                                        {col}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                        <Box mb={4}>
                            <Text mb={1}>Brand name</Text>
                            <Select
                                name="keyword_name"
                                value={mapping.keyword_name}
                                onChange={handleMappingChange}
                                placeholder="Select column"
                            >
                                {columns.map((col, index) => (
                                    <option key={index} value={col}>
                                        {col}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                        <Box mb={4}>
                            <Text mb={1}>Product Code (Optional)</Text>
                            <Select
                                name="prod_code"
                                value={mapping.prod_code}
                                onChange={handleMappingChange}
                                placeholder="Select column"
                            >
                                {columns.map((col, index) => (
                                    <option key={index} value={col}>
                                        {col}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                        <Box mb={4}>
                            <Text mb={1}>MRP (Optional)</Text>
                            <Select
                                name="mrp"
                                value={mapping.mrp}
                                onChange={handleMappingChange}
                                placeholder="Select column"
                            >
                                {columns.map((col, index) => (
                                    <option key={index} value={col}>
                                        {col}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                        <Box mb={4}>
                            <Text mb={1}>Image URL (Optional)</Text>
                            <Select
                                name="image_link"
                                value={mapping.image_link}
                                onChange={handleMappingChange}
                                placeholder="Select column"
                            >
                                {columns.map((col, index) => (
                                    <option key={index} value={col}>
                                        {col}
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        <Box mb={4}>
                            <Text mb={1}>Seller Name (Optional - enables bulk insert with seller verification)</Text>
                            <Select
                                name="seller_name"
                                value={mapping.seller_name}
                                onChange={handleMappingChange}
                                placeholder="Select column"
                            >
                                {columns.map((col, index) => (
                                    <option key={index} value={col}>
                                        {col}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="blue" mr={3} onClick={handleMappingSubmit} isLoading={isLoading}>
                            Save Mapping
                        </Button>
                        <Button variant="ghost" onClick={onClose}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal for Skipped Products */}
            <Modal isOpen={isSkippedOpen} onClose={onSkippedClose} size="2xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <VStack align="flex-start" spacing={2}>
                            <Text>⚠️ Insertion Summary</Text>
                            {insertionStats && (
                                <HStack spacing={4} fontSize="sm" color="gray.600">
                                    <Text>✅ Inserted: <strong>{insertionStats.inserted}</strong> products</Text>
                                    <Text>⏭️ Skipped: <strong>{insertionStats.skipped}</strong> products</Text>
                                </HStack>
                            )}
                        </VStack>
                    </ModalHeader>
                    <ModalBody>
                        <VStack align="stretch" spacing={4}>
                            <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={2} color="orange.600">
                                    Reason: Multiple IDs found in database for these product codes
                                </Text>
                                <Text fontSize="xs" color="gray.500" mb={4}>
                                    Please resolve the duplicate IDs in the database before inserting these products.
                                </Text>
                            </Box>

                            <TableContainer maxH="400px" overflowY="auto">
                                <Table size="sm" variant="striped">
                                    <Thead>
                                        <Tr>
                                            <Th>Product Code</Th>
                                            <Th>Product Link</Th>
                                            <Th>Seller Name</Th>
                                            <Th>Action</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {skippedData.map((item, index) => (
                                            <Tr key={index}>
                                                <Td>
                                                    <Text fontSize="xs" fontFamily="mono" maxW="150px" isTruncated>
                                                        {item.prod_code}
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    <Text
                                                        fontSize="xs"
                                                        color="blue.600"
                                                        maxW="200px"
                                                        isTruncated
                                                        cursor="pointer"
                                                        _hover={{ textDecoration: "underline" }}
                                                        onClick={() => window.open(item.url, "_blank")}
                                                    >
                                                        {item.url}
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="xs">{item.seller_name || "-"}</Text>
                                                </Td>
                                                <Td>
                                                    <Tooltip label="Copy Product Code" placement="top">
                                                        <IconButton
                                                            aria-label="Copy"
                                                            icon={<CopyIcon />}
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => copyToClipboard(item.prod_code)}
                                                        />
                                                    </Tooltip>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="outline" mr={3} onClick={downloadSkippedAsExcel}>
                            Download as Excel
                        </Button>
                        <Button colorScheme="blue" onClick={onSkippedClose}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default Index;
